import { useState } from "react";

import { ControlReassignment } from "api/generated/model";
import { useControlProgramsSuspenseQuery } from "org/control/queries/useControlProgramsQuery";
import { useControlsSuspenseQuery } from "org/control/queries/useControlsQuery";
import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";
import { loadExcelFile } from "utils/spreadsheet/loader";
import { OperationResult } from "utils/spreadsheet/types";

import { convertToControlReassignments } from "../process/converter";
import {
    AssignmentRow,
    parseSpreadsheetData,
} from "../process/spreadsheetParser";
import {
    ValidationContext,
    validateControlAssignments,
} from "../process/validator";
import { useAllUserGroupsQuery } from "../queries/useFetchUserGroupsQuery";
import { useAllUsersSuspenseQuery } from "../queries/useFetchUsersQuery";

export type ProcessorStatus = "idle" | "processing" | "success" | "error";

export type ProcessFileResult =
    | {
          success: true;
          controlReassignments: Record<string, ControlReassignment[]>;
      }
    | { success: false; errors: string[] };

interface SpreadsheetProcessorState {
    status: ProcessorStatus;
    controlReassignments: Record<string, ControlReassignment[]> | null;
}

interface InternalState extends SpreadsheetProcessorState {
    processingErrors: string[];
    parsedData: AssignmentRow[] | null;
}

interface SpreadsheetProcessor extends SpreadsheetProcessorState {
    controlReassignments: Record<string, ControlReassignment[]> | null;
    processFile: (file: File) => Promise<ProcessFileResult>;
    resetData: () => void;
}

/**
 * Hook to process and validate bulk control assignment spreadsheets
 *
 * Processing Pipeline:
 * 1. Load Excel file and convert to JSON
 * 2. Parse and validate spreadsheet structure and data types
 * 3. Validate business rules (control IDs, user IDs, tags)
 * 4. Convert valid data into control reassignments
 *
 * @returns {SpreadsheetProcessor} Spreadsheet processor interface
 * @property {ProcessorStatus} status - Current state of processing:
 *   - 'idle': Initial state, no file processed
 *   - 'processing': File is being processed
 *   - 'success': File was processed successfully
 *   - 'error': Processing failed with errors
 * @property {string[]} validationErrors - Array of validation error messages
 * @property {Record<string, ControlReassignment[]> | null} controlReassignments - Processed assignments grouped by control ID
 * @property {(file: File) => Promise<ProcessFileResult>} processFile - Process a new spreadsheet file
 *   Returns a Promise that resolves to either:
 *   - success: true with validated control reassignments
 *   - success: false with validation error messages
 * @property {() => void} resetData - Reset processor to initial state
 */
export function useSpreadsheetProcessor(): SpreadsheetProcessor {
    const { data: tagsData } = useTagsSuspenseQuery();
    const { data: controlsData } = useControlsSuspenseQuery();
    const { data: controlProgramsData } = useControlProgramsSuspenseQuery();
    const { data: usersData } = useAllUsersSuspenseQuery();
    const { data: userGroupsData } = useAllUserGroupsQuery();
    const [state, setState] = useState<InternalState>({
        status: "idle",
        parsedData: null,
        processingErrors: [],
        controlReassignments: null,
    });
    const getValidationContext = (): ValidationContext => ({
        tagsData,
        existingControlIds: new Set(
            controlsData.content.map((c) => c.resource.id)
        ),
        existingUserIds: new Set(usersData.map((u) => u.id)),
        existingUserGroupIds: new Set(userGroupsData.map((g) => g.id)),
        existingUsersWithinGroupIds: new Map(
            userGroupsData.map((g) => [
                g.id,
                new Set(g.userGroupUsers.map((u) => u.userId)),
            ])
        ),
        controlProgramIdByControlId: new Map(
            controlsData.content.map((c) => [
                c.resource.id,
                c.resource.controlProgramId,
            ])
        ),
        entityIdsByControlProgramId: new Map(
            controlProgramsData.content.map((cp) => [
                cp.resource.controlProgram.id,
                new Set(cp.resource.controlEntityIds),
            ])
        ),
    });

    const handleOperationError = <T>(
        setState: React.Dispatch<React.SetStateAction<InternalState>>,
        result: OperationResult<T>
    ): result is { success: false; errors: string[] } => {
        if (!result.success) {
            setState((prev) => ({
                ...prev,
                status: "error",
                processingErrors: result.errors,
            }));
            return true;
        }
        return false;
    };

    const processFile = async (file: File): Promise<ProcessFileResult> => {
        setState((prev) => ({ ...prev, status: "processing" }));

        const loadExcelResult = await loadExcelFile(file);
        if (handleOperationError(setState, loadExcelResult)) {
            return { success: false, errors: loadExcelResult.errors };
        }

        const initialValidation = parseSpreadsheetData(loadExcelResult.data);
        if (handleOperationError(setState, initialValidation)) {
            return { success: false, errors: initialValidation.errors };
        }

        const validationResult = validateControlAssignments(
            initialValidation.data,
            getValidationContext()
        );
        if (handleOperationError(setState, validationResult)) {
            return { success: false, errors: validationResult.errors };
        }

        const reassignments = convertToControlReassignments(
            validationResult.data,
            tagsData
        );

        setState({
            status: "success",
            parsedData: validationResult.data,
            processingErrors: [],
            controlReassignments: reassignments,
        });

        return {
            success: true,
            controlReassignments: reassignments,
        };
    };

    return {
        status: state.status,
        controlReassignments: state.controlReassignments,
        processFile,
        resetData: () =>
            setState({
                status: "idle",
                parsedData: null,
                processingErrors: [],
                controlReassignments: null,
            }),
    };
}
